import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import './modal.css';
import { useMonitoramento } from '../../context/MonitoramentoContext';
import { parseServerDate } from '../../utils/serverDate';
import { tipoFaltante } from '../../utils/faltantes';
import { playAlertSound } from '../../utils/alertSound';

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_823 || ''}/pdv_exception`,
  auth: { username: 'Portal', password: 'Portal1163' },
});

function formatarData(dataInput) {
  return parseServerDate(dataInput).toLocaleString('pt-BR');
}

function ultimaOcorrencia(records) {
  return records.reduce((max, r) => {
    const d = parseServerDate(r.recepcao);
    return d > max ? d : max;
  }, new Date(0));
}

function formatarRelativo(data) {
  if (isNaN(data.getTime()) || data.getTime() === 0) return '';
  const diffMin = Math.floor((Date.now() - data.getTime()) / 60000);
  if (diffMin < 1) return 'agora mesmo';
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `há ${diffH} h`;
  return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) +
    ' ' + data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

const FILTROS_TIPO = [
  { key: null,   label: 'Todos' },
  { key: 'NFe',  label: 'NFe' },
  { key: 'NFCe', label: 'NFCe' },
];

// Quantidade de cards (agrupados por CNPJ) exibidos na aba, para uso no botão da aba.
export function useFaltantesCount() {
  const { pdvData } = useMonitoramento();
  return useMemo(() => {
    if (!pdvData) return 0;
    const cnpjs = new Set();
    pdvData.forEach(r => {
      if (r.cnpj !== '11111111111111' && tipoFaltante(r)) cnpjs.add(r.cnpj);
    });
    return cnpjs.size;
  }, [pdvData]);
}

export function FaltantesBadge() {
  const count = useFaltantesCount();
  const [bump, setBump] = useState(false);
  const prevCountRef = useRef(count);

  useEffect(() => {
    if (count > prevCountRef.current) {
      setBump(true);
      const t = setTimeout(() => setBump(false), 500);
      prevCountRef.current = count;
      return () => clearTimeout(t);
    }
    prevCountRef.current = count;
  }, [count]);

  if (!count) return null;
  return (
    <span className={`badge bg-danger rounded-pill ms-2${bump ? ' cs-badge-bump' : ''}`}>
      {count}
    </span>
  );
}

const FaltantesNFeNFCe = () => {
  const { pdvData, lastUpdate, refresh } = useMonitoramento();
  const [filtroTipo, setFiltroTipo] = useState(null);
  const [modalData, setModalData] = useState(null); // { nome, cnpj, records }

  const registrosFaltantes = useMemo(() => {
    if (!pdvData) return [];
    return pdvData
      .filter(r => r.cnpj !== '11111111111111')
      .map(r => ({ registro: r, tipo: tipoFaltante(r) }))
      .filter(({ tipo }) => tipo !== null);
  }, [pdvData]);

  const [changedIds, setChangedIds] = useState(() => new Set());
  const prevSnapshotRef = useRef(null);
  const flashTimeoutRef = useRef(null);

  useEffect(() => {
    const snapshot = new Map();
    registrosFaltantes.forEach(({ registro }) => {
      const t = parseServerDate(registro.recepcao).getTime();
      const atual = snapshot.get(registro.cnpj);
      if (!atual) {
        snapshot.set(registro.cnpj, { count: 1, last: t });
      } else {
        atual.count += 1;
        if (t > atual.last) atual.last = t;
      }
    });
    const prev = prevSnapshotRef.current;

    if (prev) {
      const changed = new Set();
      registrosFaltantes.forEach(({ registro }) => {
        if (changed.has(registro.cnpj)) return;
        const info = snapshot.get(registro.cnpj);
        const antes = prev.get(registro.cnpj);
        if (!antes || info.count > antes.count || info.last > antes.last) {
          changed.add(registro.cnpj);
        }
      });
      if (changed.size > 0) {
        setChangedIds(changed);
        playAlertSound();
        clearTimeout(flashTimeoutRef.current);
        flashTimeoutRef.current = setTimeout(() => setChangedIds(new Set()), 4000);
      }
    }

    prevSnapshotRef.current = snapshot;
  }, [registrosFaltantes]);

  useEffect(() => () => clearTimeout(flashTimeoutRef.current), []);

  const grupos = useMemo(() => {
    const map = new Map();
    registrosFaltantes
      .filter(({ tipo }) => !filtroTipo || tipo === filtroTipo)
      .forEach(({ registro, tipo }) => {
        const key = registro.cnpj;
        if (!map.has(key)) {
          map.set(key, { cnpj: registro.cnpj, nome: registro.nome, itens: [] });
        }
        map.get(key).itens.push({ registro, tipo });
      });
    return Array.from(map.values())
      .sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
  }, [registrosFaltantes, filtroTipo]);

  const excluirRegistro = useCallback(async (id) => {
    try {
      await axiosInstance.post(`/${id}`, '');
      setModalData((prev) => prev && ({
        ...prev,
        itens: prev.itens.filter(({ registro }) => registro.controle !== id),
      }));
      refresh();
    } catch (error) {
      console.error('Erro ao excluir o registro:', error);
    }
  }, [refresh]);

  const contagemPorTipo = useCallback((tipo) =>
    registrosFaltantes.filter(({ tipo: t }) => !tipo || t === tipo).length,
    [registrosFaltantes]
  );

  return (
    <div>
      <div className="d-flex align-items-center gap-1 mb-2 flex-wrap">
        {FILTROS_TIPO.map(({ key, label }) => (
          <button
            key={String(key)}
            type="button"
            className={`btn btn-sm ${filtroTipo === key ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setFiltroTipo(key)}
          >
            {label}
            <span className="badge bg-secondary ms-2">{contagemPorTipo(key)}</span>
          </button>
        ))}
        {lastUpdate && (
          <small className="text-muted ms-auto text-nowrap">
            atualizado {lastUpdate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </small>
        )}
      </div>

      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
        {grupos.length > 0 ? (
          grupos.map(({ cnpj, nome, itens }) => (
            <div className="col" key={cnpj}>
              <div className={`card cs-exception-card cs-sev-danger h-100${changedIds.has(cnpj) ? ' cs-exception-flash' : ''}`}>

                <div className="card-body d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h5 className="card-title mb-0">{nome}</h5>
                    <span className="badge bg-danger rounded-pill fs-6">{itens.length}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="card-subtitle mb-0 text-muted">{cnpj}</h6>
                    <small className="text-muted">
                      {formatarRelativo(ultimaOcorrencia(itens.map(({ registro }) => registro)))}
                    </small>
                  </div>
                  <ul className="list-unstyled mb-2">
                    {itens.map(({ registro, tipo }, i) => (
                      <li key={i} className="d-flex align-items-start gap-2 mb-1">
                        <span className={`badge ${tipo === 'NFCe' ? 'bg-info text-dark' : 'bg-warning text-dark'}`}>
                          {tipo}
                        </span>
                        <span
                          className="cs-exception-msg"
                          dangerouslySetInnerHTML={{ __html: registro.mensagem }}
                        />
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto d-flex justify-content-end">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setModalData({ nome, cnpj, itens })}
                    >
                      Detalhes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col">
            <div className="alert alert-info" role="alert">
              Nenhum registro de NFe/NFCe faltante encontrado.
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={!!modalData}
        onRequestClose={() => { setModalData(null); refresh(); }}
        contentLabel="Detalhes"
        className="react-modal-content"
        overlayClassName="react-modal-overlay"
      >
        <div className="modal-header modal-header-wrapper">
          <h5 className="modal-title">
            Detalhes — NFe/NFCe Faltante
            {modalData?.nome && <small className="text-muted ms-2 fw-normal fs-6">— {modalData.nome}</small>}
          </h5>
          <button
            type="button"
            className="btn-close"
            aria-label="Fechar"
            onClick={() => { setModalData(null); refresh(); }}
          ></button>
        </div>
        {modalData?.itens?.length > 0 ? (
          <div className="modal-table">
            <table className="table table-hover font-sm data-table">
              <thead className="table-header">
                <tr>
                  <th>Tipo</th>
                  <th>Mensagem</th>
                  <th>Ação</th>
                </tr>
              </thead>
              <tbody>
                {modalData.itens.map(({ registro, tipo }, index) => (
                  <tr key={index}>
                    <td className="limited-cell">
                      <span className={`badge ${tipo === 'NFCe' ? 'bg-info text-dark' : 'bg-warning text-dark'}`}>
                        {tipo}
                      </span>
                      <br />
                      <small className="text-muted text-nowrap">{formatarData(registro.recepcao)}</small>
                    </td>
                    <td style={{ width: '100%' }} dangerouslySetInnerHTML={{ __html: registro.mensagem }} />
                    <td>
                      {localStorage.getItem('user-role') === 'MASTER' && (
                        <button className="btn btn-danger btn-sm" onClick={() => excluirRegistro(registro.controle)}>
                          Excluir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>Nenhum registro encontrado.</p>
        )}
      </Modal>
    </div>
  );
};

export default FaltantesNFeNFCe;
